import React, { useEffect, useState } from "react";

import AppModal from "../../components/AppModal";
import sortReversedIcon from "../../assets/sortReversedIcon.png";
import sortIcon from "../../assets/sortIcon.png";
import { i18n } from "../../translate/i18n";
import ChatTagsMultiSelect from "./ChatTagsMultiSelect";
import { ChannelFilters } from "../../helpers/ticketFilters";
import ModalMultiSelector from "../../components/ModalMultiSelector"

function getSortField(tab, sortOrder) {
	if (tab === "open") return sortOrder.sortByOpen;
	if (tab === "pending") return sortOrder.sortByPending;
	if (tab === "closed") return sortOrder.sortByClosed;
	return null;
}

function FilterSection({ title, children }) {
	return (
		<div className="tailwick-filter-modal__section">
			<p className="tailwick-filter-panel__section-title">{title}</p>
			{children}
		</div>
	);
}

function FilterCheckboxRow({ checked, color, label, onToggle }) {
	return (
		<label className="tailwick-filter-panel__option">
			<input
				type="checkbox"
				className="tailwick-filter-panel__checkbox"
				checked={checked}
				onChange={onToggle}
			/>
			{color ? (
				<span
					className="tailwick-filter-panel__color-dot"
					style={{ backgroundColor: color }}
				/>
			) : null}
			<span className="tailwick-filter-panel__option-label">{label}</span>
		</label>
	);
}

/**
 * Modal de filtros Tailwick (ordenação, setores, etiquetas).
 */
export default function ChatTicketsFilterPanel({
	open,
	onClose,
	tab,
	sortOrder,
	onSort,
	userQueues = [],
	selectedQueueIds = [],
	setSelectedQueueIds,
	tags = [],
	selectedTagIds = [],
	setSelectedTagIds,
	selectedChannels= [],
	setSelectedChannels,
}) {
	const [draftQueueIds, setDraftQueueIds] = useState(selectedQueueIds);
	const [draftTagIds, setDraftTagIds] = useState(selectedTagIds);
	const [draftChannelIds, setDraftChannelIds] = useState(selectedChannels);

	const sortField = getSortField(tab, sortOrder);
	const showSort = tab === "open" || tab === "pending" || tab === "closed";
	const sortLabel =
		sortField === "asc"
			? i18n.t("chat.layout.filterSortAsc")
			: i18n.t("chat.layout.filterSortDesc");
	const sortIconSrc = sortField === "asc" ? sortIcon : sortReversedIcon;

	const channels = ChannelFilters();

	useEffect(() => {
		if (!open) return;
		setDraftQueueIds([...(selectedQueueIds || [])]);
		setDraftTagIds([...(selectedTagIds || [])]);
		setDraftChannelIds([...(selectedChannels)])
	}, [open, selectedQueueIds, selectedTagIds,selectedChannels]);


	const toggleQueue = (queueId) => {
		if (draftQueueIds.indexOf(queueId) > -1) {
			setDraftQueueIds(draftQueueIds.filter((id) => id !== queueId));
		} else {
			setDraftQueueIds([...draftQueueIds, queueId]);
		}
	};

	const getDefaultQueueIds = () =>
		(userQueues || []).map((queue) => queue.id);

	const normalizeQueueIds = (queueIds) => {
		if (Array.isArray(queueIds) && queueIds.length > 0) {
			return queueIds;
		}
		return getDefaultQueueIds();
	};

	const handleApply = () => {
		setSelectedQueueIds(normalizeQueueIds(draftQueueIds));
		setSelectedTagIds(draftTagIds);
		setSelectedChannels(draftChannelIds);
		onClose();
	};

	const handleClear = () => {
		setDraftQueueIds(getDefaultQueueIds());
		setDraftTagIds([]);
		setDraftChannelIds([]);
	};

	const footer = (
		<div className="flex w-full flex-wrap items-center justify-end gap-2">
			<button
				type="button"
				className="btn border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary"
				onClick={handleClear}
			>
				{i18n.t("chat.layout.filterClearAll")}
			</button>
			<button type="button" className="btn bg-primary text-white hover:opacity-90" onClick={handleApply}>
				{i18n.t("chat.layout.filterApply")}
			</button>
		</div>
	);

	return (
		<AppModal
			open={open}
			onClose={onClose}
			title={i18n.t("chat.layout.filters")}
			maxWidth={480}
			bodyClassName="tailwick-filter-modal__body"
			footer={footer}
		>

			<FilterSection title={i18n.t("chat.layout.filterChannel")}>
				<ModalMultiSelector
					placeholder={i18n.t("chat.layout.selectFilterChannel")}
					selected={draftChannelIds}
					setSelect={setDraftChannelIds}
					itens={channels}
					exibicao={(channel) => {
						return channel.icon.kind === "email" ? (
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width={14}
								height={14}
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
								className="text-primary"
								aria-hidden
							>
								<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
								<polyline points="22,6 12,13 2,6" />
							</svg>
						) : (
							<img src={channel.icon.src} style={{ height: '16px' }} alt="" />
						)
					}}
				/>
			</FilterSection>

			{showSort && (
				<FilterSection title={i18n.t("chat.layout.filterSort")}>
					<button
						type="button"
						className="tailwick-filter-panel__sort-btn"
						onClick={() => onSort(tab)}
					>
						<span className="tailwick-filter-panel__sort-label">{sortLabel}</span>
						<img
							src={sortIconSrc}
							alt=""
							className="tailwick-filter-panel__sort-icon"
							width={20}
							height={20}
						/>
					</button>
				</FilterSection>
			)}

			<FilterSection title={i18n.t("chat.layout.filterQueues")}>
				<div className="tailwick-filter-modal__queue-list">
					{userQueues?.length > 0 ? (
						userQueues.map((queue) => (
							<FilterCheckboxRow
								key={queue.id}
								checked={draftQueueIds.indexOf(queue.id) > -1}
								color={queue.color}
								label={queue.name}
								onToggle={() => toggleQueue(queue.id)}
							/>
						))
					) : (
						<p className="tailwick-filter-panel__empty">
							{i18n.t("chat.layout.filterQueuesEmpty")}
						</p>
					)}
				</div>
			</FilterSection>

			<FilterSection title={i18n.t("chat.layout.filterTags")}>
				<ChatTagsMultiSelect
					tags={tags}
					selectedTagIds={draftTagIds}
					onChange={setDraftTagIds}
					menuPlacement="inline"
				/>
			</FilterSection>

		</AppModal>
	);
}
